<template>
  <div class="card">
    <PaymentElement
      v-if="IMMEDIATELY_SHOW_PAYMENT_ELEMENT"
      :newsletter="newsletter"
      :email-address="emailAddress"
    />

    <template v-else>
      <MagicLinkSection
        :newsletter="newsletter"
        :email-address="emailAddress"
        :purchase-url="purchaseUrl"
        :subscribe-button-text="subscribeButtonText"
      />
    </template>

    <button
      v-if="showFreeSubscribeLink"
      type="button"
      class="paywall-gate-free-subscribe"
      @click="emit('show-free-subscribe')"
    >
      {{ t("email.premium.action.free_subscribe") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from "vue";

import type { Newsletter } from "@/types/newsletter";

import { createTranslator, getIntlLocale } from "../../lib/utils";
import { shouldImmediatelyShowPaymentElement } from "../lib";
import MagicLinkSection from "./MagicLinkSection.vue";

const PaymentElement = defineAsyncComponent(
  () => import("./PaymentElement.vue")
);

const props = defineProps<{
  newsletter: Newsletter;
  emailAddress: string;
  showFreeSubscribeLink?: boolean;
}>();

const emit = defineEmits<{
  "show-free-subscribe": [];
}>();

const t = createTranslator(() => props.newsletter.locale);

const IMMEDIATELY_SHOW_PAYMENT_ELEMENT = computed(() =>
  shouldImmediatelyShowPaymentElement(props.newsletter)
);

const purchaseUrl = computed(
  () => `https://buttondown.com/${props.newsletter.username}/buy`
);

// PWYW is skipped: there's no single number to show without misrepresenting it.
const CADENCE_SUFFIX: Record<string, string> = {
  month: "/month",
  year: "/year",
  email: "/email",
  week: "/week",
};

const formattedHeadlinePrice = computed(() => {
  const fixed = (props.newsletter.prices || []).filter(
    (p) => p.style === "fixed" && p.amount != null && p.currency
  );
  const price =
    fixed.find((p) => p.cadence === "month") ??
    fixed.find((p) => p.cadence === "year") ??
    fixed[0];
  if (!price?.amount) return null;
  const cents = price.amount;
  const formatted = new Intl.NumberFormat(
    getIntlLocale(props.newsletter.locale),
    {
      style: "currency",
      currency: price.currency.toUpperCase(),
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }
  ).format(cents / 100);
  return `${formatted}${CADENCE_SUFFIX[price.cadence] ?? ""}`;
});

const subscribeButtonText = computed(() => {
  if (props.newsletter.subscription_button_text) {
    return props.newsletter.subscription_button_text;
  }
  if (props.newsletter.paid_subscription_free_trial_duration) {
    return t("web.subscription.action.subscribe_free_trial");
  }
  if (formattedHeadlinePrice.value) {
    return t("email.premium.action.subscribe_for_price", {
      price: formattedHeadlinePrice.value,
    });
  }
  return t("web.button.subscribe");
});
</script>
